import { type ReactElement, useEffect, useState } from 'react'
import { Autocomplete, Box, Dialog, DialogActions, DialogContent, DialogTitle } from '@mui/material'

import { Button, Icon, IconButton, TextField, Typography } from '~components/internal'
import { spacing, typography } from '~tokens/internal'
import { toTitleCase } from '~utils/textUtils'

import { type ExpectedSource } from '../../expectedSources'
import { useCustomCategories } from '../../useCustomCategories'
import SourceSearchPicker from '../SourceSearchPicker'

import './EditQuestionsDialog.scss'

export interface ScenarioEditorItem {
  queryId: string
  question: string
  expectedAnswer: string
  relevantSources: ExpectedSource[]
}

interface EditQuestionsDialogProps {
  open: boolean
  onClose: () => void
  category: string | null
  existingCategories?: string[]
  items: ScenarioEditorItem[]
  onSave: (updates: {
    category: string
    queries: Array<{
      question: string
      expected_answer: string | null
      relevant_sources: ExpectedSource[] | null
    }>
  }) => void | Promise<void>
  isUpdate?: boolean
}

function EditQuestionsDialog({
  open,
  onClose,
  category: initialCategory,
  existingCategories = [],
  items,
  onSave,
  isUpdate = false,
}: EditQuestionsDialogProps): ReactElement {
  const customCategories = useCustomCategories()
  const [localCategory, setLocalCategory] = useState<string>(initialCategory ?? '')
  const [localQuestions, setLocalQuestions] = useState<string[]>([])
  const [localAnswers, setLocalAnswers] = useState<string[]>([])
  const [localSources, setLocalSources] = useState<ExpectedSource[][]>([])
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) return
    setLocalCategory(initialCategory ?? '')
    setLocalQuestions(items.length > 0 ? items.map((item) => item.question) : [''])
    setLocalAnswers(items.length > 0 ? items.map((item) => item.expectedAnswer) : [''])
    setLocalSources(items.length > 0 ? items.map((item) => item.relevantSources) : [[]])
  }, [open, initialCategory])

  const handleAddQuestion = (): void => {
    setLocalQuestions((prev) => [...prev, ''])
    setLocalAnswers((prev) => [...prev, ''])
    setLocalSources((prev) => [...prev, []])
  }

  const handleRemoveQuestion = (index: number): void => {
    setLocalQuestions((prev) => prev.filter((_, i) => i !== index))
    setLocalAnswers((prev) => prev.filter((_, i) => i !== index))
    setLocalSources((prev) => prev.filter((_, i) => i !== index))
  }

  const handleSave = async (): Promise<void> => {
    setIsSaving(true)
    setSaveError(null)
    try {
      await onSave({
        category: localCategory,
        queries: localQuestions.map((q, i) => ({
          question: q,
          expected_answer: localAnswers[i] || null,
          relevant_sources: localSources[i]?.length ? localSources[i] : null,
        })),
      })
      onClose()
    } catch {
      setSaveError('Failed to save. Please try again.')
    } finally {
      setIsSaving(false)
    }
  }

  const isCategoryMissing = localCategory.trim() === ''

  return (
    <Dialog open={open} onClose={onClose} maxWidth='sm' fullWidth className='edit-questions-dialog'>
      <DialogTitle className='edit-questions-dialog__header'>
        {isUpdate ? 'Update' : 'Add'} Questions
        <IconButton size='small' onClick={onClose}>
          <Icon name='Close' size={typography.fontSizePx.lg} />
        </IconButton>
      </DialogTitle>

      <DialogContent className='edit-questions-dialog__content'>
        <Autocomplete
          freeSolo={!customCategories}
          options={customCategories ?? existingCategories}
          getOptionLabel={(category) => (customCategories ? toTitleCase(category) : category)}
          value={localCategory || null}
          onChange={(_, newValue) => setLocalCategory(newValue ?? '')}
          onInputChange={(_, newValue) => {
            if (!customCategories) setLocalCategory(newValue)
          }}
          renderInput={({ slotProps, ...params }) => (
            <TextField
              {...params}
              InputProps={{ ...slotProps.input }}
              inputProps={slotProps.htmlInput}
              label='Category'
              size='small'
              fullWidth
            />
          )}
        />

        {localQuestions.map((question, index) => (
          <Box key={index}>
            <Box className='edit-questions-dialog__question-label-row'>
              <Typography
                variant='body1'
                color='textSecondary'
                sx={{ fontWeight: typography.fontWeight.medium }}
              >
                Question {index + 1}
              </Typography>
              {index > 0 && (
                <IconButton size='small' onClick={() => handleRemoveQuestion(index)}>
                  <Icon name='Close' size={spacing.xs} />
                </IconButton>
              )}
            </Box>

            <Box className='edit-questions-dialog__question-fields'>
              <TextField
                label='User Prompt'
                fullWidth
                multiline
                rows={2}
                value={question}
                placeholder='Enter question...'
                onChange={(e) =>
                  setLocalQuestions((prev) => {
                    const next = [...prev]
                    next[index] = e.target.value
                    return next
                  })
                }
              />
              <TextField
                label='Expected Answer'
                fullWidth
                multiline
                rows={2}
                value={localAnswers[index] ?? ''}
                placeholder='Enter expected answer...'
                onChange={(e) =>
                  setLocalAnswers((prev) => {
                    const next = [...prev]
                    next[index] = e.target.value
                    return next
                  })
                }
              />

              <Box className='edit-questions-dialog__sources-section'>
                <Typography variant='body2' color='textSecondary'>
                  Expected Sources
                </Typography>
                <SourceSearchPicker
                  value={localSources[index] ?? []}
                  onChange={(sources) =>
                    setLocalSources((prev) => {
                      const next = prev.map((s) => [...s])
                      next[index] = sources
                      return next
                    })
                  }
                />
              </Box>
            </Box>
          </Box>
        ))}

        <Button
          variant='text'
          size='small'
          startIcon={<Icon name='addCircle' />}
          onClick={handleAddQuestion}
          sx={{ alignSelf: 'flex-start', flexShrink: 0 }}
        >
          Add another
        </Button>
      </DialogContent>

      <DialogActions>
        {saveError && (
          <Typography variant='body2' color='error' sx={{ flex: 1 }}>
            {saveError}
          </Typography>
        )}
        <Button variant='outlined' onClick={onClose} disabled={isSaving}>
          Cancel
        </Button>
        <Button
          variant='contained'
          disabled={isCategoryMissing}
          loading={isSaving}
          onClick={() => {
            void handleSave()
          }}
        >
          {isUpdate ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}

export default EditQuestionsDialog
